/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { CollectionIcon, PlusCircleIcon, ServerIcon, SwitchHorizontalIcon } from '@heroicons/react/solid'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Badge, Bubble, Button, Card, FrameInset, Pagination, Tabs, Theme } from '@nexigendigital/nxui'
import { useServiceQuery, useServicesQuery } from 'api/service'
import { ServiceStatus } from 'models/enums'
import { Service as ServiceModel } from 'models/service'
import { getServiceStatusColour } from 'helpers/utils'

import Service from 'containers/services/service'
import Information from 'containers/services/information'
import Addons from 'containers/services/addons'
import Server from 'containers/services/server'
import Overview from 'containers/services/overview'
import Cancel from 'containers/services/cancel'

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function Services() {
    const navigate = useNavigate()
    const location = useLocation()
    const [currentPage, setCurrentPage] = useState(1)
    const serviceId = parseInt(location.pathname?.split('/')[2], 10)
    const { isLoading, isSuccess, data: services } = useServicesQuery(currentPage)
    const { isError, data: service } = useServiceQuery(serviceId, { skip: !serviceId })
    const columns = useMemo(() => ([{
        Header: 'Services',
        accessor: 'services'
    }]), [])
    const servicesData = useMemo(() => {
        if (!services) {
            return []
        }

        return services.data.map(({ id, name, status, product }: ServiceModel) => ({
            services: (
                <ServiceWrapper>
                    <li onClick={() => navigate(`/services/${id}`)}>
                        <Detail>
                            <Name>
                                {name}
                            </Name>
                            <Product>
                                {product.name}
                            </Product>
                        </Detail>
                        <Badge color={getServiceStatusColour(status)}>
                            {status.toUpperCase()}
                        </Badge>
                    </li>
                </ServiceWrapper>
            )
        }))
    }, [navigate, services])
    const serviceTabs = useMemo(() => {
        const tabs = [
            {
                icon: PlusCircleIcon,
                label: 'General',
                render: <Information/>
            },
            {
                icon: PlusCircleIcon,
                label: 'Addons',
                render: <Addons/>
            }
        ]

        if (service?.status === ServiceStatus.Active) {
            return [
                ...tabs,
                {
                    icon: SwitchHorizontalIcon,
                    label: 'Server',
                    render: <Server/>
                },
                {
                    icon: CollectionIcon,
                    label: 'Cancel',
                    render: <Cancel/>
                }
            ]
        }

        return [
            ...tabs,
            {
                icon: CollectionIcon,
                label: 'Cancel',
                render: <Cancel/>
            }
        ]
    }, [service?.status])

    useEffect(() => {
        if (isSuccess && !serviceId) {
            navigate(`/services/${services.data[0].id}`)
        }

        if (isError) {
            navigate('/services')
        }
    }, [isSuccess, services, isError])

    function ServicesTable() {
        return (
            <Card
                table={{
                    conditions: {
                        loading: isLoading && 'Loading Services...',
                        search: false,
                        pagination: false
                    },
                    render: {
                        columns: () => columns,
                        data: () => servicesData
                    }
                }}
                footer={(
                    <Pagination
                        currentPage={services?.meta.current_page || 1}
                        totalCount={services?.meta.total || 1}
                        pageSize={services?.meta.per_page || 15}
                        onPageChange={(page: number) => setCurrentPage(page)}
                    />
                )}
            />
        )
    }

    return (
        <FrameInset
            inset={'split'}
            location={{ pathname: '/Services' }}
            left={{
                render: (
                    <ServicesWrapper>
                        <Bubble title={'Active Services'} desc={Array.isArray(services?.data) ? services.data.length : 0} icon={<ServerIcon/>}/>
                        <ServicesTable/>
                        <Links>
                            <Button
                                color={'primary'}
                                onClick={() => navigate('/shop')}
                            >
                                Purchase New Service
                            </Button>
                            <Button color={'flat'} onClick={() => navigate('/account')}>
                                Account
                            </Button>
                        </Links>
                    </ServicesWrapper>
                )
            }}
            right={{
                render: (
                    <DetailsWrapper>
                        <Service>
                            <Overview/>
                            <Tabs tabs={serviceTabs}/>
                        </Service>
                    </DetailsWrapper>
                )
            }}
        />
    )
}

/**********************************************************************************************************
 *   CUSTOM STYLING
 **********************************************************************************************************/
const ServicesWrapper = styled.ul`
    display: grid;
    gap: 16px;
    width: 100%;
    
    article{
        margin-bottom: 0;
    }
    
    td {
        padding: 0;
    }
`

const DetailsWrapper = styled.section`
    article {
        margin-bottom: 0;
        border-radius: 0;
    }
`

const Links = styled.div`
    width: 100%;
    display: flex;
    justify-content: space-between;
    ${props => (Theme.Functions.mediaBreakDown(props.theme['breakpoints-sm']))} {
        flex-direction: column;
        gap: 10px;
    }

    > button {
        width: 47%;
        ${props => (Theme.Functions.mediaBreakDown(props.theme['breakpoints-sm']))} {
            width: 100%;
            margin: 0;
        }
    }
`

const ServiceWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 9px;

    > li {
        display: grid;
        gap: 9px;
        padding: 18px;

        &:hover {
            cursor: pointer;
        }
    }
`

const Detail = styled.div`
    display: flex;
    gap: 5px;
`

const Name = styled.span`
    color: ${props => props.theme['black--100']};
    word-break: break-all;
`

const Product = styled.span``
